import React, { FC, useEffect, useMemo, useState } from "react";
import { DataGrid, GridSortModel } from "@mui/x-data-grid";
import { Box, TextField } from "@mui/material";
import apiClient from "../utils/apiClient";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { GridInputRowSelectionModel } from "@mui/x-data-grid/models/gridRowSelectionModel";
import { GridRowClassNameParams } from "@mui/x-data-grid/models/params/gridRowParams";
import { useTranslation } from "react-i18next";

export const DATA_TABLE_HIGHLIGHTED_ROW_CLASS = "data-table-row--highlighted";

type DataTableProps = {
  checkboxSelection?: boolean;
  isShowSearch?: boolean;
  columns: GridColDef[];
  path: string;
  refresh?: any;
  queryParams?: any;
  onSelectionChange?: (selectedRows: any[]) => void;
  onRowClick?: (row: any) => void;
  getRowClassName?: (params: GridRowClassNameParams) => string;
};

const DataTable: FC<DataTableProps> = ({
  checkboxSelection = false,
  isShowSearch = false,
  columns,
  path, // URL эндпоинта, который будет возвращать данные лидов с пагинацией
  refresh = null,
  queryParams = {},
  onSelectionChange = () => {}, // переданный callback для выбранных строк
  onRowClick,
  getRowClassName,
}) => {
  // Состояния для данных и загрузки
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  // Используем новый стейт для пагинации: paginationModel хранит номер страницы и pageSize
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  // Сохранение выбранных строк
  const [selectedRows, setSelectedRows] = useState(new Set());

  const { t } = useTranslation();

  // Стабилизируем queryParams через useMemo
  const stableQueryParams = useMemo(
    () => queryParams,
    [JSON.stringify(queryParams)],
  );

  // Функция загрузки данных с сервера
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(path, {
        params: {
          page: paginationModel.page + 1, // сервер ожидает 1-based нумерацию страниц
          pageSize: paginationModel.pageSize,
          search: searchQuery,
          sortField: sortModel.length ? sortModel[0]?.field : null,
          sortOrder: sortModel.length ? sortModel[0]?.sort : null,
          ...stableQueryParams,
        },
      });
      // Массив данных может находиться под разными ключами, используем ключ из ответа или 'clients'
      const rows = response.data[response?.data?.key ?? "clients"] ?? [];
      setData(rows);

      const apiTotal = response.data?.total;
      if (typeof apiTotal === "number" && apiTotal >= 0) {
        setTotalRows(apiTotal);
      } else {
        // Сервер не вернул total: считаем количество записей по факту загруженных
        // страниц. Если текущая страница заполнена не полностью — это последняя
        // страница и общее число записей известно точно. Если заполнена полностью —
        // допускаем, что есть ещё хотя бы одна запись, чтобы кнопка "next" была
        // доступна, но total не завышается сверх фактически подтверждённых данных.
        const loadedSoFar =
          paginationModel.page * paginationModel.pageSize + rows.length;
        setTotalRows(
          rows.length < paginationModel.pageSize
            ? loadedSoFar
            : loadedSoFar + 1,
        );
      }
    } catch (error) {
      console.error("Ошибка загрузки данных", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [
    path,
    paginationModel,
    sortModel,
    debouncedQuery,
    refresh,
    stableQueryParams,
  ]);

  // Обработчик выбора строк
  const handleRowSelectionChange = (newSelection: any) => {
    setSelectedRows(new Set(newSelection));
    if (typeof onSelectionChange === "function") {
      onSelectionChange(newSelection);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => clearTimeout(handler); // очистка таймера при каждом новом вводе
  }, [searchQuery]);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      {isShowSearch && (
        <TextField
          label={t("dataTable.search")}
          variant="outlined"
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      )}
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          rowHeight={50}
          onRowClick={
            onRowClick ? (params) => onRowClick(params.row) : undefined
          }
          getRowClassName={getRowClassName}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          // Используем новый API пагинации
          paginationModel={paginationModel}
          onPaginationModelChange={(newModel) => {
            setPaginationModel(newModel);
          }}
          pageSizeOptions={[10]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(newModel: GridSortModel) =>
            setSortModel(newModel)
          }
          checkboxSelection={checkboxSelection}
          onRowSelectionModelChange={handleRowSelectionChange}
          rowSelectionModel={
            Array.from(selectedRows) as GridInputRowSelectionModel
          }
          disableColumnMenu
          sx={{
            ...(onRowClick
              ? { "& .MuiDataGrid-row": { cursor: "pointer" } }
              : {}),
            "--DataGrid-rowBorderColor": "#D1D3D4",
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              fontSize: 12,
              lineHeight: "110%",
            },
            "& .MuiDataGrid-cell": {
              fontSize: 16,
              borderTop: "0.5px solid #D1D3D4",
            },
            [`& .${DATA_TABLE_HIGHLIGHTED_ROW_CLASS}`]: {
              backgroundColor: "#FFF3E0",
              "&:hover": {
                backgroundColor: "#FFE7C2",
              },
            },
            fontFamily: "inherit",
            borderWidth: 0,
            background: "#ffffff",
          }}
        />
      </Box>
    </Box>
  );
};

export default DataTable;
